import { ElementRef, useMemo, useRef } from 'react';
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';
import { createSnapModifier } from '@dnd-kit/modifiers';
import { css } from '@emotion/react';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { useDevToolsStore } from '@frontend/devtools';
import { ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import {
  SNAP_INCREMENT_MULTIPLIER,
  useAppointmentDragDrop,
  type DragDropState,
} from '../../../hooks/scheduler-v3/use-appointment-drag-drop';
import { reorderColumnsUsingMap } from '../../../hooks/utils/use-reordered-list';
import { useCalendarStore } from '../../../stores/calendar.store';
import { getTimezoneDetails } from '../../../utils/date-helpers';
import { NoProvidersLocationBanner } from '../../no-providers-location-banner';
import { CalendarColumnEntityListSection } from '../calendar-column-entity-list-section';
import { CalendarScrollButtons } from '../calendar-column-entity-list-section/scrollable-buttons';
import { CalendarViewContainer } from '../calendar-view-container';
import { CalendarViewMainWrapper } from '../calendar-view-main-wrapper';
import { HOURS_LANE_WIDTH, PROVIDER_COLUMN_GAP_NEW, SCROLLABLE_BUTTON_WIDTH } from '../constants';
import { EventTimelineWrapper } from '../event-timeline-wrapper';
import { HoursOfTheDay } from '../hours-of-the-day';
import { TimeProgress } from '../progress-lines';
import { useCalendarView, useCalendarViewProps, useColumnEntityReorderStore } from '../providers';
import { CalendarColumnEntity } from '../types';
import { generateHourLabels, getAdjustedOneHourInMinutes, moveUnassignedCalendarColumnEntity, zIndex } from '../utils';
import { useZoomSafeContext, checkIsHalfHourMarkersVisible } from '../zoom/zoom-context';
import { AppointmentDragDropConfirmationModal } from './appointment-drag-drop-confirmation-modal';
import { CalendarEventCardV3 } from './calendar-event-card';
import { EventCards } from './event-cards';
import { createColumnId } from './helper';
import { ScheduleDefinitionHoursIndicator } from './working-hours-indicator';

const verticalLastEventGridSectionMarginRight = SCROLLABLE_BUTTON_WIDTH + 16;

const checkIsTodaySelected = (calendarDate: string, timezone: string) => {
  const { todayDate } = getTimezoneDetails(timezone);
  return calendarDate === todayDate;
};

export const CalendarViewMainV3 = ({
  selectedScheduleRequestEventData,
  setSelectedScheduleRequestEventData,
}: {
  selectedScheduleRequestEventData: ScheduleRequestEventData | null;
  setSelectedScheduleRequestEventData: (request: ScheduleRequestEventData | null) => void;
}) => {
  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  const { pixelsPerMinute } = useZoomSafeContext();
  const { startHour, endHour, data, id, EmptyCalendarComponent, calendarDate } = useCalendarViewProps();
  const { calendarEntityType } = useCalendarStore(['calendarEntityType']);
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);
  const [columnEntityOrder] = usePreference('ScheduleCalendarViewColumnOrder');

  const { hasAiReceptionistAccess } = useAiScheduleAccess();

  const { containerState } = useCalendarView(['containerState']);

  const singleSelectedLocationTimezone = data?.length === 1 ? data?.[0]?.officeTimezone : undefined;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: pixelsPerMinute * SNAP_INCREMENT_MULTIPLIER },
    })
  );

  const {
    dragDropState,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    confirmDrop,
    cancelDrop,
  } = useAppointmentDragDrop({
    data,
    calendarDate,
    startHour,
  });

  const calendarViewWrapperRef = useRef<ElementRef<'article'>>(null);
  const providerListRef = useRef<ElementRef<'section'>>(null);
  const hourLabels = generateHourLabels(startHour, endHour, checkIsHalfHourMarkersVisible(pixelsPerMinute));
  const numberOfHours = hourLabels.length;

  const { hasColumnEntities, gridTemplateColumns } = useMemo(() => {
    const hasEntities = data?.some((office) => !!office?.columnEntities?.length);
    // this is use to set the grid-template-columns for the vertical view for both top sticky provider list and the event grid
    const gridTemplateCols = data
      ?.map((office) => {
        if (office?.columnEntities?.length) return `${office.columnEntities.length}fr`;
        // Allocate space for per-location banners only when other locations have providers
        return hasEntities ? '1fr' : '';
      })
      ?.join(' ');

    return { hasColumnEntities: hasEntities, gridTemplateColumns: gridTemplateCols };
  }, [data]);

  let isFirstColumnEntity = true;

  return (
    <>
      {!hasColumnEntities && EmptyCalendarComponent && <EmptyCalendarComponent />}
      <CalendarViewMainWrapper containerRef={calendarViewWrapperRef} id={id} disableVerticalScroll={isInReorderMode}>
        <CalendarColumnEntityListSection
          ref={providerListRef}
          calendarEntityType={calendarEntityType}
          containerRef={calendarViewWrapperRef}
          gridTemplateColumns={gridTemplateColumns}
        />
        <CalendarViewContainer numberOfHours={numberOfHours}>
          <HoursOfTheDay
            hourLabels={hourLabels}
            timezone={singleSelectedLocationTimezone}
            timezoneTextTopOffset={providerListRef.current?.clientHeight}
            pixelsPerMinute={pixelsPerMinute}
          />
          <article
            css={[
              css`
                grid-column: 1/-1;
                grid-row: 1 / -1;
                padding-left: ${HOURS_LANE_WIDTH}px;
                padding-right: ${verticalLastEventGridSectionMarginRight}px;
                display: grid;
                grid-template-columns: ${gridTemplateColumns};
                grid-template-rows: auto;
                gap: ${PROVIDER_COLUMN_GAP_NEW}px;
              `,
            ]}
          >
            {data?.map((office, index) => {
              // In multi-location setups, show a CTA banner for locations with no providers
              if (office.columnEntities?.length === 0) {
                return hasColumnEntities ? (
                  <section key={office.locationId + index} css={{ height: '100%' }}>
                    <NoProvidersLocationBanner
                      locationId={office.locationId}
                      topOffset={providerListRef.current?.clientHeight}
                    />
                  </section>
                ) : null;
              }

              // Move unassigned column entity at the end
              const sortedCalendarColumnEntities = moveUnassignedCalendarColumnEntity(office?.columnEntities);
              const isTodaySelected = checkIsTodaySelected(calendarDate, office.officeTimezone);

              const locationColumnOrderMap = columnEntityOrder?.[calendarEntityType]?.[office.locationId];
              const reorderedColumnEntities = locationColumnOrderMap
                ? reorderColumnsUsingMap(sortedCalendarColumnEntities, locationColumnOrderMap, 'entityId')
                : sortedCalendarColumnEntities;

              // For first location section on calendar, show time label when calendar is scrolled to right. For other location sections, always show time label.
              const showTimeLabel = index > 0 || !containerState.isLeft;

              const snapToGridModifier = createSnapModifier(pixelsPerMinute * SNAP_INCREMENT_MULTIPLIER);

              return (
                <section
                  key={office.officeName + index}
                  css={{ height: '100%', display: 'flex', position: 'relative' }}
                >
                  {!isInReorderMode && (
                    <>
                      {isTodaySelected && (
                        <TimeProgress
                          numberOfHours={numberOfHours}
                          officeTimezone={office.officeTimezone}
                          variant='enhanced'
                          showTimeLabel={showTimeLabel}
                          pixelsPerMinute={pixelsPerMinute}
                        />
                      )}
                    </>
                  )}
                  <DndContext
                    collisionDetection={pointerWithin}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                    modifiers={[snapToGridModifier]}
                    sensors={sensors}
                  >
                    {reorderedColumnEntities.map((columnEntity) => {
                      const officeExceptions = office?.officeExceptions || [];
                      const columnEntityEvents = columnEntity.events.map((event) => {
                        return {
                          ...event,
                          entityId: columnEntity.entityId,
                          entityName: columnEntity.entityName,
                        };
                      });
                      const columnEntityRequestEvents = (columnEntity.requestEvents || []).map((event) => {
                        return {
                          ...event,
                          entityId: columnEntity.entityId,
                          entityName: columnEntity.entityName,
                        };
                      });
                      const allEvents = [...officeExceptions, ...columnEntityEvents, ...columnEntityRequestEvents];

                      const droppableId = createColumnId(office.locationId, columnEntity.entityId);
                      const isSourceColumn = dragDropState.sourceColumnId === droppableId;
                      const isOverColumn = dragDropState.overColumnId === droppableId;
                      const isUnassignedColumn = columnEntity.entityId === '';

                      const shouldRegisterResizeObserver = isFirstColumnEntity;
                      isFirstColumnEntity = false;

                      return (
                        <EventTimelineWrapper
                          key={columnEntity.entityId}
                          columnEntityId={columnEntity.entityId}
                          columnEntityName={columnEntity.entityName}
                          locationId={office.locationId}
                          calendarDateValue={office.calendarDateValue}
                          isRegisterResizeObserver={shouldRegisterResizeObserver}
                          dragDropState={dragDropState}
                          isSourceColumn={isSourceColumn}
                          isOverColumn={isOverColumn}
                          hasPendingDrop={!!dragDropState.pendingDropPayload}
                          isUnassignedColumn={isUnassignedColumn}
                        >
                          {!isInReorderMode && (
                            <>
                              <ScheduleDefinitionHoursIndicator
                                workingHours={columnEntity.workingHours}
                                exceptions={columnEntity.availabilityExceptions}
                                debug={isDebugModeOn}
                                showStripes={!hasAiReceptionistAccess}
                              />
                              <div css={timeGridLinesStyle(numberOfHours, true, pixelsPerMinute)} />
                              <div css={{ position: 'relative', zIndex: zIndex.eventCardDefault }}>
                                <EventCards
                                  events={allEvents}
                                  locationName={office.officeName}
                                  calendarDate={office.calendarDateValue}
                                  draggedEventId={dragDropState.activeEventId}
                                  isDragging={dragDropState.isDragging}
                                  hasPendingDrop={!!dragDropState.pendingDropPayload}
                                  selectedScheduleRequestEventData={selectedScheduleRequestEventData}
                                  setSelectedScheduleRequestEventData={setSelectedScheduleRequestEventData}
                                />
                              </div>
                            </>
                          )}
                        </EventTimelineWrapper>
                      );
                    })}
                    <AppointmentDragOverlay dragDropState={dragDropState} allEvents={reorderedColumnEntities} />
                  </DndContext>
                </section>
              );
            })}
          </article>
        </CalendarViewContainer>
      </CalendarViewMainWrapper>
      <CalendarScrollButtons containerRef={calendarViewWrapperRef} />
      <AppointmentDragDropConfirmationModal
        dragDropState={dragDropState}
        show={dragDropState.pendingDropPayload !== null}
        onClose={cancelDrop}
        onConfirm={confirmDrop}
      />
    </>
  );
};

const timeGridLinesStyle = (noOfHours: number, showHorizontalBlockLines: boolean, pixelsPerMinute: number) => {
  const adjustedOneHourInMinutes = getAdjustedOneHourInMinutes(pixelsPerMinute);
  const showHalfHourLines = checkIsHalfHourMarkersVisible(pixelsPerMinute);
  return css({
    display: 'grid',
    gridRow: '1/-1',
    gridColumn: '1/-1',
    position: 'absolute',
    pointerEvents: 'none',
    gridTemplateRows: `repeat(${noOfHours}, minmax(${adjustedOneHourInMinutes}px, 1fr))`,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: zIndex.horizontalHourLines,
    ...(showHorizontalBlockLines && {
      background: showHalfHourLines
        ? `repeating-linear-gradient(to bottom,
            ${theme.colors.neutral20} 0 2px,
            transparent 2px ${adjustedOneHourInMinutes / 2}px,
            ${theme.colors.neutral10} ${adjustedOneHourInMinutes / 2}px ${adjustedOneHourInMinutes / 2 + 1}px,
            transparent ${adjustedOneHourInMinutes / 2 + 1}px ${adjustedOneHourInMinutes}px)`
        : `repeating-linear-gradient(to bottom, ${theme.colors.neutral20} 0 2px, transparent 2px ${adjustedOneHourInMinutes}px)`,
    }),
  });
};

type AppointmentDragOverlayProps = {
  dragDropState: DragDropState;
  allEvents: CalendarColumnEntity[];
};

const AppointmentDragOverlay = ({ dragDropState, allEvents }: AppointmentDragOverlayProps) => {
  const draggedEvent = allEvents
    .flatMap((colEntity) => colEntity.events)
    .find((event) => event.id === dragDropState.activeEventId);

  if (!draggedEvent || !dragDropState.isDragging) {
    return null;
  }

  // Use the actual computed dimensions from the drag event
  const overlayWidth = dragDropState.draggedEventPosition?.width;
  const overlayHeight = dragDropState.draggedEventPosition?.height;

  return (
    <DragOverlay dropAnimation={null}>
      <div
        css={{
          width: `${overlayWidth}px`,
          height: `${overlayHeight}px`,
          cursor: 'grabbing',
          willChange: 'transform',
          transform: 'translateX(-15px) rotate(-10deg)',
          transition: 'transform 0.2s ease-out',
          position: 'relative',
          // Override the absolute positioning from CalendarEventCardV3
          '& > div': {
            position: 'static',
            width: '100%',
            height: '100%',
            top: 'auto',
            left: 'auto',
          },
        }}
      >
        <CalendarEventCardV3
          showAnimated={false}
          key={`${draggedEvent?.eventId}`}
          id={draggedEvent?.eventId}
          type={'appointment'}
          title={draggedEvent?.name}
          startDate={draggedEvent?.startDate}
          startHour={draggedEvent?.startHour}
          endHour={draggedEvent?.endHour}
          visibleLaneCount={1}
          entityId={''}
          locationName={''}
          locationId={draggedEvent?.locationId}
          personStatus={draggedEvent?.personStatus}
          eventStatus={draggedEvent?.status}
          appointmentType={draggedEvent?.type}
          eventTypeColor={draggedEvent?.displayOptions?.primaryColor}
          backgroundColor={draggedEvent?.displayOptions?.backgroundColor}
        />
      </div>
    </DragOverlay>
  );
};
